import { useCallback, useMemo } from 'react';
import { useCustomerLogin, useSettings } from '@backpackjs/storefront';
import { useRouter } from 'next/router';

import { Spinner } from '~/snippets';
import { useDataLayerActions } from '~/hooks';

export function LoginForm({ setIsForgotPassword }) {
  const {
    customerLogin,
    started,
    finished,
    errors,
    success,
    reset,
    resetTimer,
  } = useCustomerLogin();
  const settings = useSettings();
  const { route } = useRouter();
  const { sendLogInEvent } = useDataLayerActions();

  const { forgotText, heading, pageHeading, unidentifiedCustomerText } = {
    ...settings?.account?.login,
  };

  const buttonText = useMemo(() => {
    if (finished) {
      resetTimer.current = setTimeout(() => {
        if (reset) reset();
        clearTimeout(resetTimer.current);
      }, 3000);
      return success ? 'Logged In' : 'Failed';
    }
    return started ? 'Logging In' : 'Log In';
  }, [started, success, finished]);

  const handleLogin = useCallback(
    async (event) => {
      event.preventDefault();
      if (started) return;
      const { email, password } = event.target;

      await customerLogin({
        email: email.value,
        password: password.value,
      });

      sendLogInEvent();
    },
    [started]
  );

  return (
    <div className="flex flex-col items-center rounded border border-border px-3 py-6 md:px-6 md:py-10">
      {route === '/account/login' && !pageHeading ? (
        <h1 className="text-title-h3 mb-6 text-center">{heading}</h1>
      ) : (
        <h2 className="text-title-h3 mb-6 text-center">{heading}</h2>
      )}

      <form
        className="mx-auto flex w-full max-w-[25rem] flex-col gap-4"
        onSubmit={handleLogin}
      >
        <label htmlFor="email">
          <span className="input-label">Email</span>
          <input
            className="input-text"
            name="email"
            placeholder="email@email.com"
            required
            type="email"
          />
        </label>

        <label htmlFor="password">
          <span className="input-label">Password</span>
          <input
            className="input-text"
            name="password"
            placeholder="••••••••"
            required
            type="password"
          />
        </label>

        <button
          aria-label="Log in to your account"
          className={`btn-primary mt-3 w-full min-w-[10rem] self-center md:w-auto ${
            started ? 'cursor-default' : ''
          }`}
          type="submit"
        >
          {started ? <Spinner width="20" /> : buttonText}
        </button>
      </form>

      <button
        aria-label={forgotText}
        className="text-underline mt-4 text-center text-sm font-bold"
        onClick={() => setIsForgotPassword(true)}
        type="button"
      >
        {forgotText}
      </button>

      {errors?.length > 0 && (
        <ul className="mt-4 flex flex-col items-center gap-1">
          {errors.map((error, index) => {
            return (
              <li key={index} className="text-center text-sm text-red-500">
                {error === 'Unidentified customer'
                  ? unidentifiedCustomerText
                  : error}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

LoginForm.displayName = 'LoginForm';
